import { observer, useLocalObservable } from 'mobx-react';
import { useEffect } from 'react';
import { observable, runInAction } from 'mobx';
import { useSearchParams } from 'react-router';
import useHistory from '../../hooks/useHistory';
import { useStore } from '../../store/store';
import Reroute from '../../components/navigation/Reroute';
import StaticPageLayout from '../../components/layouts/StaticPageLayout';
import CompanyDetails from '../../components/profiler/CompanyDetails/CompanyDetails';
import DataSourceFloatingLegend from '../../components/common/DataSourceFloatingLegend';
import GeneralLoader from '../../components/loaders/GeneralLoader';
import { API_ENDPOINTS } from '../../api/endpoints';
import { buildProfilerDetailsBody } from '../../components/profiler/CompanyDetails/utils/shareholderGraphLoading';

/**
 * The profile UUID from the first response that carries one, else the id from the URL.
 */
const resolvedCompanyId = (urlCompanyId, ...responses) =>
  responses.find((response) => response?.id)?.id || urlCompanyId;

/**
 * CompanyDetailsPage - Standalone page for company details via URL navigation
 *
 * URL pattern: /en/company-details/:companyId?tab=shareholders&ref=EntityName&searchKey=query&searchId=uuid
 * - companyId: Company ID in URL path
 * - tab: Initial tab to display (query param)
 * - ref: Back button label (query param)
 * - searchKey: Optional search query for name-based search context (query param)
 * - searchId: Optional search ID for characteristics/industry search context (query param)
 *
 * Fetches company data from API using companyId.
 * Data fetching strategy:
 * 1. When searchKey is present: uses GET_PROFILER_COMPANY_FROM_SEARCH (returns full data with header fields)
 * 2. When searchId is present (no searchKey): uses GET_PROFILER_COMPANY_SUPPLEMENTARY_DATA for header data
 *    combined with GET_PROFILER_DETAILS for detailed data (financials, shareholders, etc.)
 * 3. When neither present: uses GET_PROFILER_DETAILS first to get company name, then
 *    GET_PROFILER_COMPANY_FROM_SEARCH with that name to get header data (sequential)
 *
 * In cases 2 and 3 the GET_PROFILER_DETAILS response is handed to CompanyDetails as
 * `initialDetails`, so it does not request the same details again on mount.
 *
 * Back navigation uses browser history with optional label from ref param.
 *
 * The URL may hold the company's numeric crefo id: shareholder and subsidiary links navigate
 * by it. The BE resolves it and answers with the profile UUID, which is the company's id
 * everywhere else (watchlist, analytics, downloads), so `company.id` is that UUID.
 */
const CompanyDetailsPage = observer(() => {
  const { authStore, makeRequest } = useStore();
  const { params } = useHistory();
  const [searchParams] = useSearchParams();

  // Read URL parameters
  const companyId = params.companyId;
  const initialTab = searchParams.get('tab');
  const backRef = searchParams.get('ref');
  const searchKey = searchParams.get('searchKey');
  const searchId = searchParams.get('searchId');
  const comparablesId = searchParams.get('comparablesId'); // TD-1816: For caching comparable companies

  const state = useLocalObservable(
    () => ({
      // Track current companyId to detect URL changes
      currentCompanyId: companyId,
      // Company data from API
      company: null,
      // GET_PROFILER_DETAILS response already loaded here, keyed by company.id
      initialDetails: null,
      isLoading: true,
      error: null
    }),
    // Kept as the raw mapped response (same as a fresh fetch in CompanyDetails),
    // not converted into a deep MobX copy.
    { initialDetails: observable.ref }
  );

  // Reset state when companyId changes (handles navigation between companies)
  useEffect(() => {
    if (companyId !== state.currentCompanyId) {
      runInAction(() => {
        state.currentCompanyId = companyId;
        state.company = null;
        state.isLoading = true;
        state.error = null;
      });
    }
  }, [companyId, state]);

  // Fetch company data from API
  // Strategy - ALL cases now fetch full header data:
  // 1. searchKey present: GET_PROFILER_COMPANY_FROM_SEARCH (full data with header fields)
  // 2. searchId present (no searchKey): GET_PROFILER_COMPANY_SUPPLEMENTARY_DATA for header data
  //    + GET_PROFILER_DETAILS for detailed data, then merge (parallel)
  // 3. Neither present: GET_PROFILER_DETAILS first to get company name, then
  //    GET_PROFILER_COMPANY_FROM_SEARCH with name to get header data (sequential)
  useEffect(() => {
    if (!companyId) {
      runInAction(() => {
        state.isLoading = false;
        state.error = 'No company ID provided';
      });
      return;
    }

    // Set by the cleanup when companyId/searchKey/searchId change or the page unmounts:
    // a late response from the previous load must not overwrite the current company.
    let isStale = false;

    runInAction(() => {
      state.initialDetails = null;
      state.isLoading = true;
      state.error = null;
    });

    if (searchKey) {
      // Case 1: Name-based search - use search endpoint for full data with header fields
      makeRequest({
        endpoint: API_ENDPOINTS.GET_PROFILER_COMPANY_FROM_SEARCH,
        body: { companyId, searchKey },
        onSuccess: ({ companyData }) => {
          if (isStale) return;
          runInAction(() => {
            state.company = companyData;
            state.isLoading = false;
          });
        },
        onError: (error) => {
          if (isStale) return;
          console.error('Failed to fetch company details:', error);
          runInAction(() => {
            state.isLoading = false;
            state.error = 'Unable to load company details. Please try again.';
          });
        }
      });
    } else if (searchId) {
      // Case 2: Characteristics/industry search - need both supplementary data (header fields)
      // and detailed data (financials, shareholders, etc.)
      // Fetch both in parallel, then merge
      let supplementaryData = null;
      let detailsData = null;
      let errorOccurred = false;

      const mergeAndSetCompany = () => {
        if (errorOccurred || isStale) return;
        if (supplementaryData && detailsData) {
          runInAction(() => {
            // Merge: supplementary data provides header fields (logo, headquarters, industries)
            // details data provides everything else (financials, shareholders, comparables)
            state.company = {
              ...supplementaryData, // Header fields: website_SAFE_LOGO, headquarters, industries, etc.
              ...detailsData, // Detailed data: financials, shareholders, comparables, etc.
              id: resolvedCompanyId(companyId, detailsData, supplementaryData),
              name:
                supplementaryData.name ||
                detailsData.name ||
                detailsData.companyDetails?.name ||
                'Company'
            };
            state.initialDetails = { companyId: state.company.id, data: detailsData };
            state.isLoading = false;
          });
        }
      };

      // Fetch supplementary data (header fields via mapCompanyEntry)
      makeRequest({
        endpoint: API_ENDPOINTS.GET_PROFILER_COMPANY_SUPPLEMENTARY_DATA,
        body: { companyId, searchId },
        onSuccess: ({ companyData }) => {
          supplementaryData = companyData;
          mergeAndSetCompany();
        },
        onError: (error) => {
          if (isStale) return;
          console.error('Failed to fetch supplementary company data:', error);
          errorOccurred = true;
          runInAction(() => {
            state.isLoading = false;
            state.error = 'Unable to load company details. Please try again.';
          });
        }
      });

      // Fetch detailed data (financials, contacts, etc.). Without the shareholder graph, exactly
      // as CompanyDetails requests details: it loads the graph view itself.
      makeRequest({
        endpoint: API_ENDPOINTS.GET_PROFILER_DETAILS,
        body: buildProfilerDetailsBody(companyId, {}),
        onSuccess: (data) => {
          detailsData = data;
          mergeAndSetCompany();
        },
        onError: (error) => {
          if (isStale) return;
          console.error('Failed to fetch company details:', error);
          errorOccurred = true;
          runInAction(() => {
            state.isLoading = false;
            state.error = 'Unable to load company details. Please try again.';
          });
        }
      });
    } else {
      // Case 3: Direct navigation without search context (e.g., individual → company)
      // Step 1: Fetch details to get company name and detailed data
      // Step 2: Use company name to fetch header data via GET_PROFILER_COMPANY_FROM_SEARCH
      makeRequest({
        endpoint: API_ENDPOINTS.GET_PROFILER_DETAILS,
        body: buildProfilerDetailsBody(companyId, {}),
        onSuccess: (detailsData) => {
          if (isStale) return;
          const companyName = detailsData.name || detailsData.companyDetails?.name || 'Company';

          // Step 2: Fetch header data using company name
          makeRequest({
            endpoint: API_ENDPOINTS.GET_PROFILER_COMPANY_FROM_SEARCH,
            body: { companyId, searchKey: companyName },
            onSuccess: ({ companyData: headerData }) => {
              if (isStale) return;
              runInAction(() => {
                // Merge: header data provides logo, headquarters, industries
                // details data provides financials, shareholders, comparables
                state.company = {
                  ...headerData, // Header fields from mapCompanyEntry
                  ...detailsData, // Detailed data (financials, shareholders, etc.)
                  id: resolvedCompanyId(companyId, detailsData, headerData),
                  name: headerData.name || companyName
                };
                state.initialDetails = { companyId: state.company.id, data: detailsData };
                state.isLoading = false;
              });
            },
            onError: (headerError) => {
              if (isStale) return;
              // Header fetch failed - fall back to details only (missing header fields)
              console.warn('Failed to fetch company header data, using details only:', headerError);
              runInAction(() => {
                state.company = {
                  name: companyName,
                  ...detailsData,
                  id: resolvedCompanyId(companyId, detailsData)
                };
                state.initialDetails = { companyId: state.company.id, data: detailsData };
                state.isLoading = false;
              });
            }
          });
        },
        onError: (error) => {
          if (isStale) return;
          console.error('Failed to fetch company details:', error);
          runInAction(() => {
            state.isLoading = false;
            state.error = 'Unable to load company details. Please try again.';
          });
        }
      });
    }

    return () => {
      isStale = true;
    };
  }, [companyId, searchKey, searchId, makeRequest, state]);

  // CompanyDetails reads initialDetails once, on mount. Drop the page's reference
  // afterwards so the raw payload is not kept for the page's lifetime.
  useEffect(() => {
    if (state.initialDetails) {
      runInAction(() => {
        state.initialDetails = null;
      });
    }
  }, [state, state.initialDetails]);

  // Update page title
  useEffect(() => {
    const name = state.company?.name || 'Company details';
    document.title = `transact.digital GmbH - ${name}`;
  }, [state.company]);

  // No company ID in URL - redirect to profiler
  if (!companyId) {
    return <Reroute />;
  }

  // Show loader while fetching from API
  if (state.isLoading) {
    return (
      <StaticPageLayout page="projects-listing">
        <GeneralLoader />
      </StaticPageLayout>
    );
  }

  // Show error state
  if (state.error && !state.company) {
    return (
      <StaticPageLayout page="projects-listing">
        <div className="error-message">{state.error}</div>
      </StaticPageLayout>
    );
  }

  // Show back button if:
  // 1. ref param is present (indicates we came from somewhere)
  // 2. There's actual browser history to go back to (not a new tab)
  // New tabs opened via right-click have history.length <= 2 (varies by browser)
  const hasHistory = typeof window !== 'undefined' && window.history.length > 2;
  const showBackButton = !!backRef && hasHistory;

  return (
    <StaticPageLayout page="projects-listing">
      <CompanyDetails
        key={state.company.id}
        company={state.company}
        initialDetails={state.initialDetails}
        project={{ ownerId: authStore.currentUserId }}
        isStandalonePage={true}
        hideBackButton={!showBackButton}
        backButtonLabel={backRef ? `Back to ${backRef}` : null}
        initialTab={initialTab}
        initialComparablesId={comparablesId}
      />
      <DataSourceFloatingLegend />
    </StaticPageLayout>
  );
});

export default CompanyDetailsPage;
